import { type CSSProperties } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button, buttonClasses } from '@kawai/ui';
import {
  buildInvoiceDocument,
  invoiceDocumentTypeLabel,
  isOfflinePaymentMethod,
  todayJst,
  type InvoiceDocumentType,
} from '@kawai/shared';
import { useTenant } from '../lib/hooks/useTenant.js';
import { useBillingCharge } from '../lib/hooks/useBillingCharges.js';
import { InvoiceDocumentView } from '../components/InvoiceDocumentView.js';

/** クエリ ?type=receipt のみ領収書、それ以外（既定）は請求書。 */
function parseType(raw: string | null): InvoiceDocumentType {
  return raw === 'receipt' ? 'receipt' : 'invoice';
}

/**
 * TP-26: 適格請求書 / 領収書の印刷ビュー（saas-admin / 運営者）。
 *
 * ルート: `/billing/:tenantId/invoice/:chargeId?type=invoice|receipt`
 * billing_charges 1 件 + テナント情報から帳票データ（InvoiceDocument）を組み立てて表示し、
 * 「印刷 / PDF 保存」ボタン（window.print）で出力する。帳票は保存せず都度再構成（再発行 = 同一内容）。
 * 操作バー（戻る / 印刷ボタン）は @media print で非表示にし、帳票本体のみ印刷する。
 *
 * S-137: 帳票本体（InvoiceDocumentView）は印刷結果を変えないため v2 に載せず、このページは
 * `UiV2Scope` の外に置く（App.tsx）。操作部（ツールバー / 状態文 / 警告）だけ要素ごとに `ui-v2` を
 * 付けて v2 にする（tenant-fuel の印刷 2 ページと同じ方針）。main の余白・地色は据え置き。
 */
export function InvoicePrintPage() {
  const { tenantId = '', chargeId = '' } = useParams<{
    tenantId: string;
    chargeId: string;
  }>();
  const [searchParams] = useSearchParams();
  const type = parseType(searchParams.get('type'));
  const title = invoiceDocumentTypeLabel(type);

  const {
    data: tenant,
    isLoading: tenantLoading,
    error: tenantError,
  } = useTenant(tenantId);
  // 単一 doc 取得（O(1) read）。一覧（最新 200 件打ち切り）経由だと古い取引の再発行 deep link が
  // 偽の「見つかりません」になるため、by-id で恒久解決する。
  const {
    data: charge,
    isLoading: chargeLoading,
    error: chargeError,
  } = useBillingCharge(tenantId, chargeId);

  const isLoading = tenantLoading || chargeLoading;
  const error = tenantError ?? chargeError;

  // 領収書は入金確定（succeeded）した取引にのみ発行できる（未収金の領収書は事実に反するため
  // ハードブロック）。請求書は failed 取引にも発行し得る（未収の請求自体は正当）ので警告に留める。
  const receiptBlocked =
    type === 'receipt' && charge != null && charge.status !== 'succeeded';

  const doc =
    tenant && charge && !receiptBlocked
      ? buildInvoiceDocument({
          charge,
          recipientName: tenant.name,
          type,
          issuedDate: todayJst(),
        })
      : null;

  // TP-27: 振込先未設定（INVOICE_BANK_ACCOUNT=null）の振込請求書はプレビューのみ許し、
  // 印刷はブロックする（プレースホルダ入りの帳票を実請求書として送付する事故防止 /
  // 領収書の succeeded ハードブロックと同方針）。
  const bankAccountMissing =
    doc?.bankTransfer != null && doc.bankTransfer.account == null;

  return (
    <main style={mainStyle}>
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>
      <div className="no-print ui-v2 mx-auto mb-4 flex max-w-[720px] flex-wrap items-center justify-between gap-3">
        <Link
          to={`/billing/${tenantId}`}
          className={buttonClasses({ v2: true, variant: 'paper', size: 'sm' })}
        >
          ← 決済明細へ戻る
        </Link>
        <h1 className="text-label font-medium text-ink-3">{title}プレビュー</h1>
        <Button
          type="button"
          v2
          size="sm"
          onClick={() => window.print()}
          disabled={!doc || bankAccountMissing}
        >
          印刷 / PDF 保存
        </Button>
      </div>

      {isLoading && (
        <p role="status" aria-live="polite" className={STATUS}>
          読み込み中…
        </p>
      )}

      {error && (
        <p role="alert" className={ERROR}>
          データの取得に失敗しました: {error.message}
        </p>
      )}

      {!isLoading && !error && !charge && (
        <p role="alert" className={ERROR}>
          対象の取引（charge: {chargeId}）が見つかりません。
        </p>
      )}

      {!isLoading && !error && receiptBlocked && (
        <p role="alert" className={ERROR}>
          領収書は入金確定（成功）した取引にのみ発行できます（この取引: {charge!.status}
          ）。
        </p>
      )}

      {/* TP-27/TP-34: 手動請求（銀行振込・口座振替）の pending は「請求済・支払待ち」＝
          請求書発行の正常タイミングのため警告しない。取消済（failed）は固有の文言で警告する。 */}
      {!isLoading &&
        !error &&
        charge &&
        !receiptBlocked &&
        charge.status !== 'succeeded' &&
        (isOfflinePaymentMethod(charge.paymentMethod) ? (
          charge.status !== 'pending' && (
            <p className={`no-print ${WARN}`} role="note">
              この請求は{charge.status === 'refunded' ? '返金済み' : '取消済み'}（
              {charge.status}）です。発行する場合は内容を確認してください。
            </p>
          )
        ) : (
          <p className={`no-print ${WARN}`} role="note">
            この取引は「成功」以外（{charge.status}
            ）です。請求書は内容を確認のうえ発行してください。
          </p>
        ))}

      {bankAccountMissing && (
        <p className={`no-print ${ERROR}`} role="alert">
          振込先口座が未設定のため印刷をブロックしています（プレビューのみ）。
          `@kawai/shared` の INVOICE_BANK_ACCOUNT に実口座情報を設定してください。
        </p>
      )}

      {doc && <InvoiceDocumentView doc={doc} />}
    </main>
  );
}

/** 操作部の状態文 / エラー / 警告（v2 / 幅は帳票と同じ 720px）。帳票本体には当てない。 */
const STATUS = 'ui-v2 py-8 text-center text-body text-ink-3';
const ERROR = 'ui-v2 mx-auto max-w-[720px] text-body text-danger-ink';
const WARN =
  'ui-v2 mx-auto mb-4 max-w-[720px] rounded-lg bg-warning-tint px-[18px] py-3 text-label text-warning-ink';

// main の余白・地色は印刷結果に関わるため据え置き（帳票のハーネス突合の前提）。
const mainStyle: CSSProperties = {
  fontFamily: 'system-ui, sans-serif',
  padding: '1.5rem',
  background: 'hsl(var(--muted))',
  minHeight: '100vh',
};
